import React, { useEffect, useMemo, useState } from 'react';
import { FaStar, FaFilter, FaSearch, FaTimes, FaHeart } from 'react-icons/fa';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import mainService from '../services/mainService';
import config from '../services/config.json';
import useScrollToTop from '../hooks/useScrollToTop';
import { useToast } from '../contexts/ToastContext';
import './Products.css';

const normalizeProduct = (raw, categoryName) => {
  return {
    id: raw.Id || raw.id,
    Name: raw.Name || raw.name || 'Product',
    Price: Number(raw.Price ?? raw.price ?? 0),
    OfferPrice: raw.OfferPrice ?? raw.offerPrice ?? null,
    AvgRating: raw.AvgRating ?? raw.avgRating ?? raw.rating ?? 0,
    Image1: raw.Image1 || raw.image1 || raw.image || '',
    Code: raw.Code || raw.code || raw.Subcategory || raw.subcategory || '',
    Category: categoryName || raw.Category || raw.category || ''
  };
};

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  // Read new query parameters
  const categoryId = searchParams.get('id');
  const categoryType = searchParams.get('type');
  const categoryName = decodeURIComponent(searchParams.get('name') || '');
  const selectedSubcategory = decodeURIComponent(searchParams.get('subcategory') || '');
  
  // For backward compatibility, also check old 'category' parameter
  const selectedCategory = categoryName || decodeURIComponent(searchParams.get('category') || '');

  const [allProducts, setAllProducts] = useState([]);
  const [categoriesWithSubs, setCategoriesWithSubs] = useState([]);
  const [topPicksProducts, setTopPicksProducts] = useState([]);
  const [topSellingProducts, setTopSellingProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wishlistItems, setWishlistItems] = useState(new Set());

  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [sortBy, setSortBy] = useState('name');
  const [showFilters, setShowFilters] = useState(false);

  // Scroll to top when filters or search changes
  useScrollToTop([searchTerm, priceRange, sortBy, selectedSubcategory]);

  // Load wishlist items from localStorage on component mount
  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const user = JSON.parse(userData);
      const customerId = user.CustomerId || user.Id;
      if (customerId) {
        // Use customer-specific localStorage keys
        const savedWishlist = localStorage.getItem(`wishlistItems_${customerId}`);
        if (savedWishlist) {
          setWishlistItems(new Set(JSON.parse(savedWishlist)));
        }
      }
    }
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        setError(null);
        const [catsWithProducts, catsWithSubs, randomProducts, topSelling] = await Promise.all([
          mainService.categoriesWithProducts(),
          mainService.categoriesWithSubs(),
          mainService.getRandomProducts(),
          mainService.TopSellingProducts()
        ]);
        
        const flattened = Array.isArray(catsWithProducts)
          ? catsWithProducts.flatMap(cat => (Array.isArray(cat.Products) ? cat.Products.map(p => normalizeProduct(p, cat.Name)) : []))
          : [];
        setAllProducts(flattened);
        setCategoriesWithSubs(Array.isArray(catsWithSubs) ? catsWithSubs : []);
        
        const normalizedTopPicks = Array.isArray(randomProducts) ? randomProducts.map(p => normalizeProduct(p, p.Category || '')) : [];
        setTopPicksProducts(normalizedTopPicks);
        
        const normalizedTopSelling = Array.isArray(topSelling) ? topSelling.map(p => normalizeProduct(p, p.Category || '')) : [];
        setTopSellingProducts(normalizedTopSelling);
        
        if (flattened.length > 0) {
          const prices = flattened.map(p => Number(p.Price || 0)).filter(n => !Number.isNaN(n));
          const min = Math.max(0, Math.min(...prices));
          const max = Math.max(...prices);
          if (isFinite(min) && isFinite(max) && max >= min) setPriceRange([min, max]);
        }
      } catch (err) {
        console.error('Failed to load products:', err);
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const priceBounds = useMemo(() => {
    const prices = (allProducts || []).map(p => Number(p.OfferPrice && p.OfferPrice < p.Price ? p.OfferPrice : p.Price)).filter(n => !Number.isNaN(n));
    if (!prices.length) return { min: 0, max: 10000 };
    return { min: Math.floor(Math.min(...prices)), max: Math.ceil(Math.max(...prices)) };
  }, [allProducts]);

  const baseProducts = useMemo(() => {
    // If type is "latest", show latest products (first 20 products as they come from API)
    if (categoryType === 'latest') {
      return allProducts.slice(0, 20);
    }

    // If type is "top picks", show the random/top picks list
    if (categoryType === 'top picks') {
      return topPicksProducts;
    }

    // If type is "top selling", show the top selling list
    if (categoryType === 'top selling') {
      return topSellingProducts;
    }

    // If type is "all", show all products
    if (categoryType === 'all') {
      return allProducts;
    }

    // If we have a specific category, filter products by that category
    if (selectedCategory && selectedCategory !== 'all') {
      return allProducts.filter(product => 
        product.Category && product.Category.toLowerCase().includes(selectedCategory.toLowerCase())
      );
    }

    // Default: show all products
    return allProducts;
  }, [allProducts, topPicksProducts, topSellingProducts, categoryType, selectedCategory]);

  const products = useMemo(() => {
    let filtered = baseProducts;

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(product =>
        product.Name.toLowerCase().includes(term) ||
        product.Code.toLowerCase().includes(term) ||
        product.Category.toLowerCase().includes(term)
      );
    }

    // Apply price filter
    filtered = filtered.filter(product => {
      const price = product.OfferPrice && product.OfferPrice < product.Price ? product.OfferPrice : product.Price;
      return price >= priceRange[0] && price <= priceRange[1];
    });

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return (a.OfferPrice && a.OfferPrice < a.Price ? a.OfferPrice : a.Price) - 
                 (b.OfferPrice && b.OfferPrice < b.Price ? b.OfferPrice : b.Price);
        case 'price-high':
          return (b.OfferPrice && b.OfferPrice < b.Price ? b.OfferPrice : b.Price) - 
                 (a.OfferPrice && a.OfferPrice < a.Price ? a.OfferPrice : a.Price);
        case 'rating':
          return b.AvgRating - a.AvgRating;
        case 'name':
        default:
          return a.Name.localeCompare(b.Name);
      }
    });

    return filtered;
  }, [baseProducts, searchTerm, priceRange, sortBy]);

  const openDetails = (product) => {
    const encodedName = encodeURIComponent(product.Name);
    navigate(`/product/${product.id}/${encodedName}`);
  };



  const toggleWishlist = async (product, e) => {
    e.stopPropagation();
    const productId = product.id;
    
    const userData = localStorage.getItem('userData');
    if (!userData) {
      showToast('Please login to continue', 'info', { actionLabel: 'Login', actionTo: '/login' });
      return;
    }

    try {
      const user = JSON.parse(userData);
      const customerId = user.CustomerId || user.Id;

      // Manage localStorage wishlistProducts (full product details) - customer-specific
      const customerSpecificKey = `wishlistProducts_${customerId}`;
      const stored = localStorage.getItem(customerSpecificKey);
      const parsed = stored ? JSON.parse(stored) : [];
      const wishlistProducts = Array.isArray(parsed) ? parsed : [];

      const existingIndex = wishlistProducts.findIndex(p => String(p.Id) === String(productId));

      if (existingIndex !== -1) {
        // Remove from wishlist
        wishlistProducts.splice(existingIndex, 1);
        const newWishlist = new Set(wishlistItems);
        newWishlist.delete(productId);
        setWishlistItems(newWishlist);
        localStorage.setItem(`wishlistItems_${customerId}`, JSON.stringify([...newWishlist]));
      } else {
        // Add to wishlist
        const item = {
          Id: productId,
          Name: product.Name,
          Price: product.Price,
          OfferPrice: product.OfferPrice,
          Image: product.Image1,
          Category: product.Code,
          Rating: product.AvgRating
        };
        wishlistProducts.push(item);
        const newWishlist = new Set(wishlistItems);
        newWishlist.add(productId);
        setWishlistItems(newWishlist);
        localStorage.setItem(`wishlistItems_${customerId}`, JSON.stringify([...newWishlist]));
      }

      localStorage.setItem(customerSpecificKey, JSON.stringify(wishlistProducts));
      
      // Refresh wishlist count in header
      if (window.refreshWishlistCount) {
        window.refreshWishlistCount();
      }
      
      // Navigate to product detail page instead of wishlist page
      const encodedName = encodeURIComponent(product.Name);
      navigate(`/product/${productId}/${encodedName}`);
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      showToast('Failed to update wishlist', 'error');
    }
  };

  const updateSubcategory = (subcategory) => {
    const newParams = new URLSearchParams(searchParams);
    if (subcategory) {
      newParams.set('subcategory', subcategory);
    } else {
      newParams.delete('subcategory');
    }
    setSearchParams(newParams);
  };

  if (loading) {
    return (
      <div className="products-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading products...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="products-page">
        <div className="container">
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button className="btn btn-primary" onClick={() => window.location.reload()}>Try Again</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="products-page">
      <div className="container">
        {/* Page Header */}
        <div className="page-header">
          <h1 className="page-title">
            {categoryType === 'latest' ? 'Latest Products' : categoryType === 'top picks' ? 'Top Picks For You' : categoryType === 'top selling' ? 'Top Selling Products' : categoryType === 'all' ? 'Our Products' : selectedCategory ? selectedCategory : 'Our Products'}
          </h1>
          <p className="page-subtitle">Discover our amazing collection of products</p>
         
        </div>


        {/* Search and Filters */}
        <div className="products-controls">
          <div className="search-section">
            <div className="search-container">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <button 
              className="filter-toggle-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter />
              Filters
            </button>
          </div>

          {/* Filters Panel */}
          <div className={`filters-panel ${showFilters ? 'show' : ''}`}>
            <div className="filters-panel-inner">
              {selectedCategory && (
                <div className="filter-group filter-group-category">
                  <span className="filter-label">Category</span>
                  <span className="filter-value">{selectedCategory}</span>
                </div>
              )}

              <div className="filter-group filter-group-price">
                <span className="filter-label">Price range</span>
                <div className="price-range-display">
                  <span className="price-min">AED {Math.floor(priceRange[0])}</span>
                  <span className="price-sep">–</span>
                  <span className="price-max">AED {Math.ceil(priceRange[1])}</span>
                </div>
                <div className="price-sliders">
                  <div className="price-slider-wrap">
                    <label className="price-slider-label">Min</label>
                    <input
                      type="range"
                      min={priceBounds.min}
                      max={priceBounds.max}
                      value={priceRange[0]}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        setPriceRange([val, Math.max(val, priceRange[1])]);
                      }}
                      className="price-slider"
                      aria-label="Minimum price"
                    />
                  </div>
                  <div className="price-slider-wrap">
                    <label className="price-slider-label">Max</label>
                    <input
                      type="range"
                      min={priceBounds.min}
                      max={priceBounds.max}
                      value={priceRange[1]}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        setPriceRange([Math.min(val, priceRange[0]), val]);
                      }}
                      className="price-slider"
                      aria-label="Maximum price"
                    />
                  </div>
                </div>
              </div>

              <div className="filter-group filter-group-sort">
                <label className="filter-label" htmlFor="sort-select">Sort by</label>
                <select
                  id="sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="filter-select"
                  aria-label="Sort products"
                >
                  <option value="name">Name A–Z</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="rating">Rating</option>
                </select>
              </div>

              <button
                type="button"
                className="clear-filters-btn"
                onClick={() => {
                  setSearchTerm('');
                  setPriceRange([priceBounds.min, priceBounds.max]);
                  setSortBy('name');
                  updateSubcategory('');
                }}
              >
                <FaTimes />
                Clear filters
              </button>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="products-section">
          {products.length > 0 ? (
            <>
              <div className="products-info">
                <p>Showing {products.length} of {baseProducts.length} products</p>
              </div>
              <div className="products-grid">
                {products.map((product, index) => {
                  const isInWishlist = wishlistItems.has(product.id);
                  
                  return (
                    <div key={product.id} className="product-card" onClick={() => openDetails(product)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') openDetails(product); }}>
                    <div className="product-image">
                        <img
                          src={product.Image1 ? (String(product.Image1).startsWith('http') ? product.Image1 : (config.apiUrl + product.Image1)) : 'https://via.placeholder.com/300x300/333/fff?text=Product'}
                          alt={product.Name}
                          loading={index < 6 ? 'eager' : 'lazy'}
                          decoding="async"
                        />
                        <div className="product-actions">
                      <button 
                            className={`wishlist-btn ${isInWishlist ? 'in-wishlist' : ''}`}
                            onClick={(e) => toggleWishlist(product, e)}
                            title={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
                      >
                            <FaHeart />
                      </button>
                        </div>
                    </div>
                    <div className="product-info">
                        <span className="product-category">{product.Code || 'General'}</span>
                        <h3 className="product-name">{product.Name}</h3>
                        
                      <div className="product-price">
                          {product.OfferPrice && product.OfferPrice < product.Price ? (
                            <>
                              <span className="original-price">AED {product.Price}</span>
                              <span className="price">AED {product.OfferPrice}</span>
                            </>
                          ) : (
                            <span className="price">AED {product.Price}</span>
                          )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="no-products">
              <div className="no-products-content">
                <h3>No products found</h3>
                <p>Try adjusting your search or filter criteria</p>
                <Link 
                  className="btn btn-primary"
                  to="/products"
                  onClick={() => setSearchParams({}, { replace: true })}
                >
                  View All Products
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Products;
